import React, { useState, useEffect } from 'react';
import { Shield, Trash2, LogIn, Eye, EyeOff, Users, AlertTriangle, CheckCircle, LogOut, UserPlus, Mail, List, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [testerStatus, setTesterStatus] = useState(null);
  const [isClearing, setIsClearing] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  
  // Manual access state
  const [showGrantAccess, setShowGrantAccess] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [isGranting, setIsGranting] = useState(false);
  const [accessList, setAccessList] = useState(null);
  const [showAccessList, setShowAccessList] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    setIsCheckingAuth(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const storedToken = localStorage.getItem('admin_token');
      
      const response = await fetch(`${backendUrl}/api/admin/verify`, {
        method: 'POST',
        credentials: 'include',
        headers: storedToken ? {
          'Authorization': `Bearer ${storedToken}`
        } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.authenticated) {
          setIsAuthenticated(true);
          setAdminToken(storedToken);
          fetchTesterStatus(storedToken);
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success && data.token) {
        setIsAuthenticated(true);
        setAdminToken(data.token);
        localStorage.setItem('admin_token', data.token);
        toast.success('Admin access granted');
        fetchTesterStatus(data.token);
      } else {
        toast.error(data.message || 'Invalid password');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setPassword('');
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/admin/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setAdminToken(null);
    setTesterStatus(null);
    setAccessList(null);
    toast.success('Logged out successfully');
  };

  const fetchTesterStatus = async (token) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/tester/status`, {
        headers: token ? {
          'Authorization': `Bearer ${token}`
        } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setTesterStatus(data);
      }
    } catch (error) {
      console.error('Error fetching tester status:', error);
    }
  };

  const handleClearTesterAccess = async () => {
    if (!window.confirm('Are you sure you want to revoke all tester access? This cannot be undone.')) {
      return;
    }

    setIsClearing(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/tester/clear`, {
        method: 'DELETE',
        headers: adminToken ? {
          'Authorization': `Bearer ${adminToken}`
        } : {}
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Cleared ${data.cleared_count} tester access record(s)`);
        localStorage.removeItem('digital_safety_tester');
        fetchTesterStatus(adminToken);
      } else {
        toast.error('Failed to clear tester access');
      }
    } catch (error) {
      console.error('Error clearing tester access:', error);
      toast.error('Failed to clear tester access');
    } finally {
      setIsClearing(false);
    }
  };

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }

    setIsGranting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/grant-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify({
          user_email: grantEmail.trim(),
          reason: grantReason.trim() || 'Manual unlock by admin'
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Access granted to ${grantEmail}`);
        setGrantEmail('');
        setGrantReason('');
        setShowGrantAccess(false);
        fetchAccessList();
      } else {
        toast.error(data.message || 'Failed to grant access');
      }
    } catch (error) {
      console.error('Error granting access:', error);
      toast.error('Failed to grant access');
    } finally {
      setIsGranting(false);
    }
  };

  const fetchAccessList = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/access-list`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAccessList(data);
      }
    } catch (error) {
      console.error('Error fetching access list:', error);
      toast.error('Failed to fetch access list');
    }
  };

  const handleRevokeAccess = async (accessId) => {
    if (!window.confirm('Are you sure you want to revoke this access?')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/admin/revoke-access/${accessId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast.success('Access revoked');
        fetchAccessList();
      } else {
        toast.error('Failed to revoke access');
      }
    } catch (error) {
      console.error('Error revoking access:', error);
      toast.error('Failed to revoke access');
    }
  };

  // Loading state
  if (isCheckingAuth) {
    return (
      <div className="max-w-md mx-auto fade-in">
        <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center animate-pulse">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <p className="text-text-muted">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto fade-in">
        <div className="bg-white rounded-2xl border border-stone-200 p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-text mb-2">
              Admin Access
            </h1>
            <p className="text-text-muted">
              Enter your admin password to continue
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-stone-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  placeholder="Enter admin password"
                  data-testid="admin-password-input"
                  autoFocus
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !password.trim()}
              className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-4 font-semibold"
              data-testid="admin-login-btn"
            >
              {isLoading ? (
                <>Loading...</>
              ) : (
                <>
                  <LogIn className="w-4 h-4 mr-2" />
                  Access Admin Panel
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="max-w-3xl mx-auto fade-in">
      <header className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl text-text">
                Admin Panel
              </h1>
              <p className="text-text-muted text-sm">
                Manage access and course settings
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="rounded-xl"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      {/* Manual Access Management */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <UserPlus className="w-5 h-5 text-primary" />
            <h2 className="font-heading font-bold text-lg text-text">
              Manual Access Control
            </h2>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAccessList(!showAccessList);
                if (!accessList) fetchAccessList();
              }}
              className="rounded-lg"
            >
              <List className="w-4 h-4 mr-1" />
              {showAccessList ? 'Hide' : 'View'} List
            </Button>
            <Button
              size="sm"
              onClick={() => setShowGrantAccess(!showGrantAccess)}
              className="bg-primary text-white hover:bg-primary-600 rounded-lg"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Grant Access
            </Button>
          </div>
        </div>

        <p className="text-text-muted text-sm mb-4">
          Manually grant course access to users when payment confirmation fails or for special cases.
        </p>

        {/* Grant Access Form */}
        {showGrantAccess && (
          <form onSubmit={handleGrantAccess} className="bg-stone-50 rounded-xl p-4 mb-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                User Email *
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <Input
                  type="email"
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="user@example.com"
                  className="pl-10"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-muted mb-1">
                Reason (optional)
              </label>
              <Input
                type="text"
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder="e.g., Payment confirmed via Stripe dashboard"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={isGranting || !grantEmail.trim()}
                className="bg-success text-white hover:bg-success/90 rounded-lg"
              >
                {isGranting ? 'Granting...' : 'Grant Access'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowGrantAccess(false)}
                className="rounded-lg"
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Access List */}
        {showAccessList && accessList && (
          <div className="space-y-4">
            {/* Manual Access */}
            <div>
              <h3 className="text-sm font-medium text-text-muted mb-2">
                Manual Access ({accessList.total_manual})
              </h3>
              {accessList.manual_access.length === 0 ? (
                <p className="text-sm text-text-muted bg-stone-50 rounded-lg p-3">No manual access records</p>
              ) : (
                <div className="space-y-2">
                  {accessList.manual_access.map((record) => (
                    <div key={record.id} className="flex items-center justify-between bg-stone-50 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-text text-sm">{record.user_email}</p>
                        <p className="text-xs text-text-muted">
                          {record.reason} • {new Date(record.granted_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {record.is_active ? (
                          <>
                            <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">Active</span>
                            <button
                              onClick={() => handleRevokeAccess(record.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Revoke access"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-xs bg-stone-200 text-stone-600 px-2 py-1 rounded">Revoked</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Paid Access */}
            <div>
              <h3 className="text-sm font-medium text-text-muted mb-2">
                Paid Access ({accessList.total_paid})
              </h3>
              {accessList.paid_access.length === 0 ? (
                <p className="text-sm text-text-muted bg-stone-50 rounded-lg p-3">No payment records</p>
              ) : (
                <div className="space-y-2">
                  {accessList.paid_access.map((record, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                      <div>
                        <p className="font-medium text-text text-sm">Payment #{record.session_id?.slice(-8)}</p>
                        <p className="text-xs text-text-muted">
                          ${record.amount} • {new Date(record.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-xs bg-success/10 text-success px-2 py-1 rounded">Paid</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tester Access Management */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="w-5 h-5 text-primary" />
          <h2 className="font-heading font-bold text-lg text-text">
            Tester Access Management
          </h2>
        </div>

        {testerStatus?.tester_enabled === false && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Tester feature is disabled</span>
            </div>
            <p className="text-sm text-amber-600 mt-1">
              Set TESTER_CODE environment variable to enable tester access.
            </p>
          </div>
        )}

        <div className="bg-stone-50 rounded-xl p-4 mb-4">
          <h3 className="text-sm font-medium text-text-muted mb-3">Current Status</h3>
          {testerStatus?.is_tester ? (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-text">Active Tester Access</p>
                <p className="text-sm text-text-muted">
                  Expires: {testerStatus?.expires_at ? new Date(testerStatus.expires_at).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Unknown'}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-success" />
              </div>
              <div>
                <p className="font-medium text-text">No Active Tester Access</p>
                <p className="text-sm text-text-muted">
                  {testerStatus?.is_expired ? 'Previous tester access has expired' : 'No tester codes have been used'}
                </p>
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={handleClearTesterAccess}
          variant="outline"
          className="w-full border-red-200 text-red-600 hover:bg-red-50 rounded-xl py-4 font-semibold"
          disabled={isClearing || !testerStatus?.is_tester || testerStatus?.tester_enabled === false}
          data-testid="clear-tester-btn"
        >
          {isClearing ? (
            <>Clearing...</>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Revoke All Tester Access
            </>
          )}
        </Button>
      </div>

      {/* Info Box */}
      <div className="bg-primary/5 rounded-2xl p-6">
        <h3 className="font-heading font-bold text-text mb-2">Quick Reference</h3>
        <ul className="space-y-2 text-sm text-text-muted">
          <li>• <strong>Manual Access:</strong> Use for payment issues or special cases</li>
          <li>• <strong>Tester Access:</strong> 7-day temporary access via tester code</li>
          <li>• Manual access persists until revoked; tester access expires automatically</li>
          <li>• Testers see "TEST" watermark on certificate</li>
        </ul>
      </div>
    </div>
  );
}
